import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
} from "@mui/material";

// Carrier and Service mappings based on Zippy API documentation
export const CARRIER_SERVICES = {
  DTDC: ["B2C SMART EXPRESS", "B2C PRIORITY"],
  Delhivery: ["Surface", "Express", "Surface 250gm"],
  Ecom: ["Express"],
  Xpressbees: [
    "Surface Xpressbees 0.5 K.G",
    "Xpressbees 1 K.G",
    "Xpressbees 2 K.G",
    "Xpressbees 5 K.G",
    "Xpressbees 10 K.G",
    "Air Xpressbees 0.5 K.G",
    "Xpressbees Same Day Delivery",
    "Xpressbees Next Day Delivery",
    "Express Reverse",
  ],
  "DTDC-B2B": [
    "SURFACE ECONOMY",
    "SURFACE EXPRESS",
    "AIR CARGO",
    "EXPRESS",
    "STANDARD_AIR",
    "GROUND_EXPRESS",
    "B2B SMART EXPRESS",
    "B2C PRIORITY",
    "B2C SMART EXPRESS",
    "COD",
    "PRIORITY",
  ],
  Bluedart: ["Surface", "Air", "Surface 1.5kg"],
  ATS: [
    "Amazon Shipping Standard",
    "Amazon Shipping Express (Ground) - Forward",
    "Amazon Shipping Express (Ground) - Reverse",
    "Amazon Shipping Air - Forward",
    "Amazon Shipping Air - Reverse",
  ],
  ZippyyBluedart: ["Surface", "Air", "Plus Surface"],
  ZippyyxEkart: ["Surface 0.5kg", "Surface 1.0kg"],
  EkartPremium: ["Surface 2.0kg"],
  Shadowfax: ["Surface"],
  Blitz: ["Surface 0.5kg", "Air 0.5kg", "Surface 2.0kg", "Air 2.0kg"],
};

export const getServiceOptions = (carrier) => {
  return CARRIER_SERVICES[carrier] || ["Surface", "Express", "Air"];
};

const ALL_CARRIERS = [
  "DTDC",
  "Delhivery",
  "Ecom",
  "Xpressbees",
  "DTDC-B2B",
  "Bluedart",
  "ATS",
  "ZippyyBluedart",
  "ZippyyxEkart",
  "EkartPremium",
  "Shadowfax",
  "Blitz",
];

/**
 * Reusable Carrier and Service Selection Dialog
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Callback when dialog is closed
 * @param {string} selectedCarrier - Currently selected carrier
 * @param {string} selectedService - Currently selected service
 * @param {function} onCarrierChange - Callback when carrier changes
 * @param {function} onServiceChange - Callback when service changes
 * @param {array} availableCarriers - List of available carriers from serviceability check
 * @param {boolean} loadingServiceability - Whether serviceability check is in progress
 * @param {array} quickQuoteRates - List of quick quote rate objects (from Zippy rates API)
 * @param {boolean} loadingQuickQuote - Whether quick quote call is in progress
 * @param {number|null} packageWeight - Current package weight in kg
 * @param {{length: number|null, width: number|null, height: number|null}} packageDimensions - Current package dimensions in cm
 * @param {function} onPackageChange - Callback when package info changes (triggers quote refresh)
 * @param {function} onRateSelect - Callback when a specific rate card is selected
 * @param {boolean} generatingLabel - Whether label generation is in progress
 * @param {function} onGenerate - Callback when Generate Label button is clicked
 * @param {string} componentName - Name of the component using this dialog (for logging)
 * @param {string} productName - Product name to display in the dialog
 */
export default function CarrierServiceDialog({
  open,
  onClose,
  selectedCarrier,
  selectedService,
  onCarrierChange,
  onServiceChange,
  availableCarriers = [],
  loadingServiceability = false,
  quickQuoteRates = [],
  loadingQuickQuote = false,
  packageWeight = null,
  packageDimensions = {},
  onPackageChange,
  onRateSelect,
  generatingLabel = false,
  onGenerate,
  componentName = "CarrierServiceDialog",
  productName = null,
}) {
  const [localWeight, setLocalWeight] = React.useState(packageWeight ?? 1);
  const [localLength, setLocalLength] = React.useState(
    packageDimensions?.length ?? 12
  );
  const [localWidth, setLocalWidth] = React.useState(
    packageDimensions?.width ?? 12
  );
  const [localHeight, setLocalHeight] = React.useState(
    packageDimensions?.height ?? 12
  );

  React.useEffect(() => {
    if (open) {
      if (packageWeight != null) {
        setLocalWeight(packageWeight);
      }
      if (packageDimensions) {
        if (packageDimensions.length != null) {
          setLocalLength(packageDimensions.length);
        }
        if (packageDimensions.width != null) {
          setLocalWidth(packageDimensions.width);
        }
        if (packageDimensions.height != null) {
          setLocalHeight(packageDimensions.height);
        }
      }
    }
  }, [
    open,
    packageWeight,
    packageDimensions?.length,
    packageDimensions?.width,
    packageDimensions?.height
  ]);

  const handlePackageBlur = () => {
    if (!onPackageChange) return;
    const next = {
      weightKg: Number(localWeight) || 1,
      length: Number(localLength) || 12,
      width: Number(localWidth) || 12,
      height: Number(localHeight) || 12,
    };
    onPackageChange(next);
  };

  const handleRateClick = (rate) => {
    const carrier = rate.carrier || rate.carrier_name || rate.carrierService || "";
    const service = rate.service || rate.carrierService || "";

    console.log(`Quick quote rate selected (${componentName}):`, {
      carrier,
      service,
      rate,
    });

    if (carrier) {
      onCarrierChange(carrier);
    }
    if (service) {
      onServiceChange(service);
    }
    if (onRateSelect) {
      onRateSelect(rate);
    }
  };

  const formatCurrency = (value, currency = "INR") => {
    const num = parseFloat(value || "0");
    if (Number.isNaN(num)) return "N/A";
    return `${currency} ${num.toFixed(2)}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={false}
      onBackdropClick={onClose}
      PaperProps={{
        className: "bg-white dark:bg-gray-900 rounded-2xl shadow-soft-lg border border-gray-100 dark:border-gray-700"
      }}
    >
      <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white pb-3 pt-6 px-6">
        <div className="inline-block mb-2 px-3 py-1 bg-primary-100 dark:bg-primary-900/30 rounded-full">
          <span className="text-primary-700 dark:text-primary-300 text-sm font-semibold">
            🚚 Select Carrier & Service
          </span>
        </div>
        <br />
        Select Carrier & Service
      </DialogTitle>
      <DialogContent className="px-6 py-4">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* Product name */}
          {productName && (
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">
                {productName}
              </Typography>
            </Box>
          )}
          {/* Package information used for quote */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Weight (kg)
              </Typography>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={localWeight}
                onChange={(e) => setLocalWeight(e.target.value)}
                onBlur={handlePackageBlur}
                className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Length (cm)
              </Typography>
              <input
                type="number"
                min="1"
                step="1"
                value={localLength}
                onChange={(e) => setLocalLength(e.target.value)}
                onBlur={handlePackageBlur}
                className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Width (cm)
              </Typography>
              <input
                type="number"
                min="1"
                step="1"
                value={localWidth}
                onChange={(e) => setLocalWidth(e.target.value)}
                onBlur={handlePackageBlur}
                className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Height (cm)
              </Typography>
              <input
                type="number"
                min="1"
                step="1"
                value={localHeight}
                onChange={(e) => setLocalHeight(e.target.value)}
                onBlur={handlePackageBlur}
                className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </Box>
          </Box>

          {(loadingServiceability || loadingQuickQuote) && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                {loadingQuickQuote
                  ? "Fetching rates..."
                  : "Checking available carriers..."}
              </Typography>
            </Box>
          )}

          {quickQuoteRates && quickQuoteRates.length > 0 ? (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select a carrier & service with estimated rate:
              </Typography>
              <Grid container spacing={2}>
                {quickQuoteRates.map((rate) => {
                  const key = rate.id || `${rate.carrier}-${rate.service}`;
                  const carrier =
                    rate.carrier ||
                    rate.carrier_name ||
                    rate.carrierService ||
                    "Carrier";
                  const service = rate.service || rate.carrierService || "";
                  const price =
                    rate.rate || rate.forwardFreightCharges || "0.00";
                  const currency = rate.currency || "INR";
                  const etaDays =
                    rate.est_delivery_days != null
                      ? rate.est_delivery_days
                      : rate.delivery_days;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <Card
                        className={`card-modern cursor-pointer transition-all duration-200 ${
                          selectedCarrier === carrier &&
                          selectedService === service
                            ? "ring-2 ring-primary-600 dark:ring-primary-400 border-primary-600 dark:border-primary-400"
                            : "hover:shadow-lg"
                        }`}
                        onClick={() => handleRateClick(rate)}
                      >
                        <CardContent className="p-4">
                          <Typography
                            variant="subtitle2"
                            noWrap
                            title={carrier}
                            className="text-gray-900 dark:text-white font-semibold"
                          >
                            {carrier}
                          </Typography>
                          {service && (
                            <Typography
                              variant="body2"
                              noWrap
                              title={service}
                              className="text-gray-600 dark:text-gray-400"
                            >
                              {service}
                            </Typography>
                          )}
                          <div className="mt-2">
                            <Typography variant="h6" className="text-gray-900 dark:text-white font-bold">
                              {formatCurrency(price, currency)}
                            </Typography>
                          </div>
                          {etaDays != null && (
                            <div className="mt-2">
                              <Chip
                                size="small"
                                label={`ETA: ${etaDays} day${
                                  etaDays === 1 ? "" : "s"
                                }`}
                                className="bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border-primary-300 dark:border-primary-600"
                              />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          ) : (
            <>
              {availableCarriers.length > 0 ? (
                <Alert severity="success" sx={{ mt: 1 }}>
                  {availableCarriers.length} carrier(s) available for this
                  route. Rates are not available; please select carrier and
                  service manually.
                </Alert>
              ) : !loadingServiceability && !loadingQuickQuote ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Serviceability / rate check unavailable. All carriers will be
                  tried by the backend; if generation fails, try again later.
                </Alert>
              ) : null}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions className="px-6 pb-6 pt-2">
        <Button
          onClick={onClose}
          className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onGenerate}
          disabled={!selectedCarrier || !selectedService || generatingLabel}
          className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case disabled:opacity-50"
        >
          {generatingLabel ? <CircularProgress size={20} className="text-white" /> : "Generate Label"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

